import { OptionallyVisible } from 'components';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import merchantsApi from 'services/merchants';
import { BridgeFiatRail, FiatPayoutSettingsPayload, MerchantInfo } from 'services/merchants/types';
import * as Yup from 'yup';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { Buttons } from 'components/modal/styles';

import { ReduxToolkitError } from 'types/http';

import { getServerSideValidationDetails } from 'utils/error';

import { Delimiter, Label, Section } from '../styles';
import LABELS, { KEYS } from './keys';
import { ErrorText, SecondaryText, StyledMultilineInput } from './styles';

interface MemoProps {
  skippable?: boolean;
  initialValue?: string;
  merchant: MerchantInfo;
  paymentRail: BridgeFiatRail;
  proceed: () => void;
  onClose: () => void;
}

const Memo = ({ proceed, onClose, skippable = true, merchant, paymentRail, initialValue }: MemoProps) => {
  const { t } = useTranslation();
  const [payoutSettingsMutation, { isLoading, error, reset }] = merchantsApi.useUpdateMerchantPayoutSettingsMutation();

  const maxMemoLength = KEYS.PAYMENT_RAIL_MEMO_LENGTH[paymentRail].maxLength;
  const minMemoLength = KEYS.PAYMENT_RAIL_MEMO_LENGTH[paymentRail].minLength;
  const incorrectMemoLengthMessage = t(LABELS.MEMO_VALIDATION_ERROR, { min: minMemoLength, max: maxMemoLength });

  const memoValidator = Yup.string()
    .test('max-lines', t(LABELS.MEMO_LINE_COUNT_ERROR, { max: 4 }), (value) => !value || value.split('\n').length <= 4)
    .test(
      'line-length',
      incorrectMemoLengthMessage,
      (value) =>
        !value ||
        value.split('\n').every((line) => {
          const len = line.trim().length;
          return len >= minMemoLength && len <= maxMemoLength;
        }),
    )
    .test(
      'allowed-characters',
      t(LABELS.MEMO_INVALID_CHARACTERS),
      (value) => !value || value.split('\n').every((line) => /^[a-zA-Z0-9/\-?:().,'+ ]*$/.test(line)),
    );

  const onSubmit = async () => {
    const errors = await form.validateForm();
    form.setErrors(errors);
    const hasErrors = Object.keys(form.errors).length > 0;
    if (hasErrors) {
      return;
    }

    if (form.values[KEYS.FORM_FIELDS.MEMO].length === 0 && skippable) {
      proceed();
      return;
    }

    const payload: FiatPayoutSettingsPayload = {
      merchantId: merchant.uuid,
      payoutMemo: form.values[KEYS.FORM_FIELDS.MEMO],
    };

    const response = await payoutSettingsMutation(payload);

    if ('error' in response) {
      return;
    }

    proceed();
  };

  const formConfiguration = {
    initialValues: {
      [KEYS.FORM_FIELDS.MEMO]: initialValue || '',
    },
    validationSchema: Yup.object().shape({
      [KEYS.FORM_FIELDS.MEMO]: memoValidator,
    }),
    onSubmit,
    validateOnChange: true,
  };
  const form = useFormik(formConfiguration);

  const onMemoChange = (value: string) => {
    if (error) {
      reset();
    }
    form.setFieldValue(KEYS.FORM_FIELDS.MEMO, value);
  };

  const proceedLabel = form.values[KEYS.FORM_FIELDS.MEMO].length || !skippable ? t(LABELS.ACTIONS.SAVE) : t(LABELS.ACTIONS.SKIP);
  const hasError = (field: string) => Boolean(form.errors[field]);
  const isProceedDisabled = hasError(KEYS.FORM_FIELDS.MEMO);

  const getErrorMessage = () => {
    const validationMessage = getServerSideValidationDetails(error, KEYS.UNASSOCIATED_ERROR)?.payoutMemo;

    return form.errors[KEYS.FORM_FIELDS.MEMO] || validationMessage || (error as ReduxToolkitError)?.data?.errorMessage;
  };

  return (
    <>
      <Section>
        <Label>{t(LABELS.MEMO_LABEL)}</Label>
        <SecondaryText>
          {t(LABELS.MEMO_SUB_LABEL, {
            paymentRail: t(LABELS.PAYMENT_RAIL_OPTIONS[paymentRail]),
            maxLength: KEYS.PAYMENT_RAIL_MEMO_LENGTH[paymentRail].maxLength,
            minLength: KEYS.PAYMENT_RAIL_MEMO_LENGTH[paymentRail].minLength,
          })}
        </SecondaryText>
        <StyledMultilineInput
          name={KEYS.FORM_FIELDS.MEMO}
          value={form.values[KEYS.FORM_FIELDS.MEMO]}
          placeholder={t(LABELS.MEMO_PLACEHOLDER)}
          fullWidth
          rows={5}
          onChange={onMemoChange}
        />
        <OptionallyVisible visible={hasError(KEYS.FORM_FIELDS.MEMO) || Boolean(error)}>
          <ErrorText>{getErrorMessage()}</ErrorText>
        </OptionallyVisible>
      </Section>
      <Delimiter />
      <Buttons>
        <Button secondary onClick={onClose}>
          {t(LABELS.ACTIONS.CANCEL)}
        </Button>
        <ButtonWithLoading isLoading={isLoading} type="submit" disabled={isProceedDisabled} primary onClick={onSubmit}>
          {proceedLabel}
        </ButtonWithLoading>
      </Buttons>
    </>
  );
};

export default Memo;
